import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  Approval,
  CreateApprovalPayload,
  UpdateApprovalPayload,
} from 'api/approval';
import { DocumentApprovalAPI } from 'api/document/document-approval';
import { DocumentId, ProjectId } from 'api/models';
import { tenMinutes } from './constants';

export const getDocumentApprovalsQueryKey = (
  documentId: DocumentId,
  projectId: ProjectId,
) => ['approvals', ...projectId, ...documentId];

export const useDocumentApprovalsQuery = (
  documentId: DocumentId,
  projectId: ProjectId,
) => {
  return useQuery(
    getDocumentApprovalsQueryKey(documentId, projectId),
    () => DocumentApprovalAPI.getApprovalList(projectId, documentId),
    {
      staleTime: tenMinutes,
    },
  );
};

export const useCreateDocumentApprovalMutation = (
  documentId: DocumentId,
  projectId: ProjectId,
): UseMutationResult<Approval, unknown, CreateApprovalPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Approval, unknown, CreateApprovalPayload>(
    (payload) =>
      DocumentApprovalAPI.createApproval(projectId, documentId, payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getDocumentApprovalsQueryKey(documentId, projectId),
        );
      },
    },
  );
};

export const useUpdateDocumentApprovalMutation = (
  documentId: DocumentId,
  projectId: ProjectId,
): UseMutationResult<Approval, unknown, UpdateApprovalPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Approval, unknown, UpdateApprovalPayload>(
    (payload) =>
      DocumentApprovalAPI.updateApproval(
        projectId,
        documentId,
        payload.approvalId,
        payload,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getDocumentApprovalsQueryKey(documentId, projectId),
        );
      },
    },
  );
};

export const useDeleteDocumentApprovalMutation = (
  documentId: DocumentId,
  projectId: ProjectId,
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (approvalId) =>
      DocumentApprovalAPI.deleteApproval(projectId, documentId, approvalId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getDocumentApprovalsQueryKey(documentId, projectId),
        );
      },
    },
  );
};
